import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';

import moment from 'moment';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { DataViewModule } from 'primeng/dataview';
import { StepperModule } from 'primeng/stepper';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { PanelModule } from 'primeng/panel';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ContactPaymentRequest, ContactServiceInfoResponse, itemDescription, KeycardInfo, LocationSettings, LocationSettingsWithFlags, paymentMethod, State, TennatAccount, TennatAccountContactInfo, VehicleInfo } from '../../../../../../express-tRPC/src/shared/types';;
import { CommonSharedService } from '../../services/common-shared.service';
import { CommunicationService } from 'src/app/services/communication/communication.service';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PaymentMethodSelectionComponent } from '../payment-method-selection/payment-method-selection.component';

@Component({
  selector: 'app-contacts-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MessagesModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    DropdownModule,
    CalendarModule,
    ToastModule,
    TagModule,
    DataViewModule,
    StepperModule,
    TooltipModule,
    CardModule,
    RadioButtonModule,
    CheckboxModule,
    PanelModule,
    ConfirmDialogModule,
    PaymentMethodSelectionComponent
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './contacts-list.component.html',
  styleUrl: './contacts-list.component.scss'
})
export class ContactsListComponent {

  @Input('source') source: string | undefined;
  @Input('allowContactSelection') allowContactSelection!: boolean;
  @Input('suite') suite: string | undefined;
  @Input('account') account: string | undefined;
  
  @Output() mergeContactEvent = new EventEmitter<{source: TennatAccountContactInfo, primaryContact: TennatAccountContactInfo}>();
  @Output() contactUpdatedEvent = new EventEmitter<boolean>();
  
  @Output() contactsSelectedEvent = new EventEmitter<TennatAccountContactInfo[]>();

  tenantContactServiceInfo: ContactServiceInfoResponse | undefined;
  tenantContacts: TennatAccountContactInfo[] = [];
  terminatedTenantContacts: TennatAccountContactInfo[] = [];
  autopaySettings: LocationSettingsWithFlags | void = undefined;
  creditCardServiceCharge: number = 0;
  echeckServiceCharge: number = 0;

  states: State[] | undefined = [];

  loading: boolean = false;
  contactsUpdating: boolean = false;
  paymentInProgress: boolean = false;

  contactFormGroup!: FormGroup;
  showContactModal: boolean = false;
  showEditContactModal: boolean = false;
  showAttendantAddContactModal: boolean = false;

  formSubmitted: boolean = false;
  currentStepIndex: number = 0;

  terminateAccountDate: Date | undefined = undefined;
  showCancelKeycardModal: boolean = false;
  serviceChargeAmount: number = 0;

  validPaymentSettings: boolean = true;
  tokenizationSourceKey: string = '';
  tokenizationAPIURL: string = '';

  maxDate: Date = new Date();

  selectedPaymentMethod: paymentMethod | undefined;

  selectedContactToUpdate: TennatAccountContactInfo | undefined = undefined;

  // If contact keycard date is this many days after the start of the month then prorate
  prorateAfterDays: number = 16;
  isProrated: boolean = false;

  availableKeycardActions: any[] = [
    // { name: "Add", code: 'add' },
    { name: "Check", code: 'check' },
    // { name: "Terminate", code: 'terminate' },
  ];

  allowedVehicles: number = 3;
  validatingVehicleInfo: boolean = false;

  activationFeesRateDescription: itemDescription | undefined = undefined;
  contactActivationFeePaymentRequest: ContactPaymentRequest | undefined = undefined;
  contactPaymentRequest: ContactPaymentRequest  | undefined = undefined;

  allowAddEditContact: boolean = true;
  primaryContact: TennatAccountContactInfo | undefined;

  isLPRActive: boolean = false;
  
  @ViewChild(PaymentMethodSelectionComponent) paymentMethodSelectionComponent!: PaymentMethodSelectionComponent;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private commonService: CommonSharedService,
    private commService: CommunicationService
  ) { }

  ngOnInit() {

    this.primaryContact = undefined;

    this.initContactForm();

    this.checkLPRStatus();
    this.loadDescriptionLists();
    this.getStates();
    this.getLocationSettings();
  }

  async checkLPRStatus() {
    try {
      const result = await this.commService.checkLPRStatus();
      this.isLPRActive = result.isLPRActive;
    } catch (error: any) {
      console.error('Error checking LPR status:', error);
      // Default to not active if there's an error
      this.isLPRActive = false;
    }
  }

  async loadDescriptionLists() {
    this.loading = true;
    let tenantContactServiceInfoResponse: ContactServiceInfoResponse | void = await this.commService.getTenantContactServiceInfo()?.catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.loading = false;
    });
    this.tenantContactServiceInfo = tenantContactServiceInfoResponse || undefined;

    this.loadTenantInfo();
  }

  async loadTenantInfo() {
    this.loading = true;

    let accountDetailsResult: TennatAccount | void = await this.commService.getAccountDetails(this.suite, this.account).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.loading = false;
    });

    if (accountDetailsResult) {
      this.tenantContacts = accountDetailsResult.contactInfo;

      this.tenantContacts = this.tenantContacts.map((contact: TennatAccountContactInfo) => {
        const rate = contact.rate;
        // Find the internal rate code
        const rateIndex = this.tenantContactServiceInfo?.available_invoice_items?.findIndex((item: any) => item.code === rate);

        if (rateIndex !== -1) {
          // A rate was found and assigned
          contact.rateDisplay = this.tenantContactServiceInfo?.available_invoice_items[rateIndex!].description;
          contact.rate_amount = this.tenantContactServiceInfo?.available_invoice_items[rateIndex!].unitprice;
        } else {
          // No rate found or assigned
          contact.rateDisplay = '';
          contact.rate_amount = '';
        }
        return contact;
      })
      this.filterTenantContacts();

    }
    this.loading = false;
    
    //Set contactUpdated event to false
    this.contactUpdatedEvent.emit(false);
  }

  async getLocationSettings(): Promise<void> {

    let locationSettingResult: LocationSettingsWithFlags | void = await this.commService.getLocationSettings()?.catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
        key: 'general-toast-message'
      });
    });

    if(locationSettingResult) {

      this.autopaySettings = locationSettingResult;

      this.validPaymentSettings = locationSettingResult?.has_valid_payment_settings || false;

      this.tokenizationSourceKey = locationSettingResult?.accept_blue_tokenization_source_key || '';
      this.tokenizationAPIURL = locationSettingResult?.accept_blue_tokenization_api_url || '';
      if (this.tokenizationAPIURL) {
        this.commonService.initializeAcceptBlueTokenizationURL(this.tokenizationAPIURL);
      }

      if(locationSettingResult?.['activation_fee_validation_rate']) {
        this.activationFeesRateDescription = locationSettingResult?.['activation_fee_validation_rate'];
      }

      this.creditCardServiceCharge = Number(this.autopaySettings.service_charge !== "" ? this.autopaySettings.service_charge : this.autopaySettings.credit_card_service_charge_global);
      this.echeckServiceCharge = Number(this.autopaySettings.echeck_service_charge !== "" ? this.autopaySettings.echeck_service_charge : this.autopaySettings.echeck_service_charge_global);

    }
  }

  async getStates(): Promise<void> {
    let statesResult: State[] | void = await this.commService.getStates().catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
    })

    if (statesResult) {
      this.states = statesResult;
    }
  }

  initContactForm(): void {
    this.contactFormGroup = new FormGroup({
      contactName: new FormControl<string | null>('', []),
      contactFirstName: new FormControl<string | null>('', [Validators.required]),
      contactLastName: new FormControl<string | null>('', [Validators.required]),
      description: new FormControl<string | null>('', []),
      rate: new FormControl<string | null>('', [Validators.required]),
      ar_type: new FormControl<string | null>('', []),
      phones: new FormControl<string | null>('', []),
      extensions: new FormControl<string | null>('', []),
      emails: new FormControl<string | null>('', []),
      state: new FormControl<string | null>('', []),
      effectiveDates: new FormControl<string | null>('', []),
      terminationDates: new FormControl<string | null>('', []),
      isTerminated: new FormControl<boolean>(false, []),
      keycardsInfo: new FormArray([]),
      vehiclesInfo: new FormArray([])
    });
  }

  devAutofillForm(): void {
    // Generate random test data
    const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Emma', 'Robert', 'Lisa', 'William', 'Mary'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Martinez', 'Wilson'];
    const descriptions = ['Employee', 'Manager', 'Supervisor', 'Administrator', 'Contractor', 'Visitor', 'Executive', 'Staff'];

    const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
    const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const randomDescription = descriptions[Math.floor(Math.random() * descriptions.length)];

    // Generate random phone number
    const randomPhone = `${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`;

    // Generate random extension
    const randomExtension = Math.floor(Math.random() * 9000) + 1000;

    // Generate random email
    const randomEmail = `${randomFirstName.toLowerCase()}.${randomLastName.toLowerCase()}${Math.floor(Math.random() * 100)}@example.com`;

    // Set form values
    this.contactFormGroup.patchValue({
      contactFirstName: randomFirstName,
      contactLastName: randomLastName,
      description: randomDescription,
      phones: randomPhone,
      extensions: randomExtension.toString(),
      emails: randomEmail
    });

    // Set rate if available rates exist
    if (this.tenantContactServiceInfo?.available_invoice_items && this.tenantContactServiceInfo.available_invoice_items.length > 0) {
      const randomRateIndex = Math.floor(Math.random() * this.tenantContactServiceInfo.available_invoice_items.length);
      const randomRate = this.tenantContactServiceInfo.available_invoice_items[randomRateIndex];
      this.contactFormGroup.patchValue({
        rate: randomRate
      });
    }

    // Set state if states are available
    if (this.states && this.states.length > 0) {
      const randomStateIndex = Math.floor(Math.random() * this.states.length);
      const randomState = this.states[randomStateIndex].code;
      this.contactFormGroup.patchValue({
        state: randomState
      });
    }

    // Set AR type if available
    if (this.tenantContactServiceInfo?.available_ar_types && this.tenantContactServiceInfo.available_ar_types.length > 0) {
      const randomARTypeIndex = Math.floor(Math.random() * this.tenantContactServiceInfo.available_ar_types.length);
      const randomARType = this.tenantContactServiceInfo.available_ar_types[randomARTypeIndex];
      this.contactFormGroup.patchValue({
        ar_type: randomARType.code
      });
    }

    // Add random keycard info if not already present
    if (this.KeycardsInfoFormControlArray.controls.length === 0 && this.source !== 'attendant-portal') {
      this.addKeycardsInfoGroup(false);
      const keycardGroup = this.getKeycardsInfoFormGroup(0);
      if (keycardGroup) {
        keycardGroup.patchValue({
          keycard: `KC${Math.floor(Math.random() * 900000) + 100000}`,
          active_keycard: true
        });
      }
    }

    // Add random vehicle info
    if (this.VehiclesInfoFormControlArray.controls.length === 0) {
      this.addVehiclesInfoGroup();
      const vehicleGroup = this.getVehiclesInfoFormGroup(0);
      if (vehicleGroup) {
        const makes = ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes', 'Audi', 'Tesla'];
        const models = ['Camry', 'Accord', 'F-150', 'Silverado', 'X5', 'C-Class', 'A4', 'Model 3'];
        const colors = ['Black', 'White', 'Silver', 'Blue', 'Red', 'Gray', 'Green'];

        vehicleGroup.patchValue({
          make: makes[Math.floor(Math.random() * makes.length)],
          model: models[Math.floor(Math.random() * models.length)],
          license: `${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 9000) + 1000}`,
          color: colors[Math.floor(Math.random() * colors.length)],
          year: (new Date().getFullYear() - Math.floor(Math.random() * 10)).toString()
        });
      }
    }

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: 'Dev Mode',
      detail: 'Form populated with test data',
      life: 3000
    });
  }

  primaryContactOptions() {
    const contactOptions: any[] = structuredClone(this.tenantContacts);
    const currentEditContactIndex = this.tenantContacts.findIndex((contact) => contact.contactName == this.selectedContactToUpdate?.contactName);

    if(currentEditContactIndex !== -1 ) {
      contactOptions[currentEditContactIndex]['disabled'] = true;
    }
    return contactOptions;
  }
  
  get KeycardsInfoFormControlArray() {
    return this.contactFormGroup.get('keycardsInfo') as FormArray;
  }

  getKeycardsInfoFormGroup(index: number): FormGroup {
    return this.KeycardsInfoFormControlArray.controls[index] as FormGroup;
  }

  removeKeycardsInfoGroup(index: number): void {
    this.KeycardsInfoFormControlArray.removeAt(index);
  }

  addKeycardsInfoGroup(diasbled: boolean = true, isNew: boolean = true): void {
    this.KeycardsInfoFormControlArray.push(new FormGroup({
      keycards: new FormControl<string | null>({ value: '', disabled: diasbled }, [Validators.required]),
      facility_code: new FormControl<string | null>({ value: '', disabled: diasbled }, [Validators.required]),
      active_keycard: new FormControl<boolean>({ value: false, disabled: diasbled }, []),
      keycard_action: new FormControl<string>('', []),
      is_new:  new FormControl<boolean>(isNew),
    }))
  }

  get VehiclesInfoFormControlArray() {
    return this.contactFormGroup.get('vehiclesInfo') as FormArray;
  }

  getVehiclesInfoFormGroup(index: number): FormGroup {
    return this.VehiclesInfoFormControlArray.controls[index] as FormGroup;
  }

  removeVehiclesInfoGroup(index: number): void {
    this.VehiclesInfoFormControlArray.removeAt(index);
  }

  addVehiclesInfoGroup(): void {
    this.VehiclesInfoFormControlArray.push(new FormGroup({
      plate: new FormControl<string | null>('', [Validators.required]),
      make: new FormControl<string | null>('', []),
      model: new FormControl<string | null>('', []),
      year: new FormControl<string | null>('', []),
      color: new FormControl<string | null>('', []),
    }
    ))
  }

  filterTenantContacts(): void {
    this.terminatedTenantContacts = this.tenantContacts.filter((contact: TennatAccountContactInfo) => {
      if (contact.terminationDates) {
        const isTeminationDateInPast = moment.utc(contact.terminationDates).isBefore(moment.utc());
        /**
         * Consider contact as terminated if termination date is in PAST 
         * OR 
         * matching surepark member in INACTIVE
         **/
        return isTeminationDateInPast || (contact?.surepark_member_details?.active === false);
      }
      return false;
    });
    this.tenantContacts = this.tenantContacts.filter((contact: TennatAccountContactInfo) => {
      if (contact.terminationDates) {
        const isTeminationDateIsTodayOrInFuture = moment.utc(contact.terminationDates).isSameOrAfter(moment.utc());
        const matchingSureparkMemberDetails = contact?.surepark_member_details;
        /**
         * Consider contact as ACTIVE if termination date is in FUTURE
         * AND
         * matching surepark member in ACTIVE
         **/
        if(matchingSureparkMemberDetails) {
          return isTeminationDateIsTodayOrInFuture && (matchingSureparkMemberDetails.active === true);
        }

        /**
         * We reach here because we dont have matching surepark member details
         * So we will just check if termination date is future
         **/
        return isTeminationDateIsTodayOrInFuture;
      }
      return true;
    });
  }

  paymentMethodSelected(pMethod: paymentMethod): void {
    //Check if existing method is selected
    //If id not found means user have selected new payment method so inthat case we have to find service charge based on selected payment method type
    if(!pMethod?.id) {
      this.selectedPaymentMethod = undefined;
      this.serviceChargeAmount = pMethod.payment_method_type == "Credit Card" ? Number(this.creditCardServiceCharge || 0) : (Number(this.echeckServiceCharge) || 0);
    } else {
      this.selectedPaymentMethod = pMethod;
      this.serviceChargeAmount = pMethod?.service_charge || 0;
    }
  }

  getRateLabel(rate: any): string {
    const rateIndex = this.tenantContactServiceInfo?.available_invoice_items?.findIndex((item: any) => item.code === rate);
    if (rateIndex! !== -1) {
      return this.tenantContactServiceInfo?.available_invoice_items[rateIndex!].description!
    }
    return rate;
  }

  getUnitPrice(unitprice: string, appendCurrencySymbol: boolean = false): number | string {
    const price = this.commonService.convertCurrencyStringToNumber(unitprice);
    return appendCurrencySymbol ? ('$' + price) : price;
  }

  getServiceCharge(amount: string, checkProrated: boolean = true, includeActivationFee: boolean = false): number {
    let price = this.commonService.convertCurrencyStringToNumber(amount);

    if (checkProrated) {
      price = this.calcProrate(Number(price));
    }
    if(includeActivationFee) {
      price += this.contactActivationFeePaymentRequest!.amount;
    }
    return ((price * Number(this.serviceChargeAmount || 0)) / 100);
  }

  getTotalAmount(amount: string, includeServiceCharge: boolean = false, checkProrated: boolean = true, includeActivationFee: boolean = false): number {
    const price = this.commonService.convertCurrencyStringToNumber(amount);

    let proratedAmount = Number(price);
    if(checkProrated) {
      proratedAmount = this.calcProrate(Number(price));
    }
    if(includeActivationFee) {
      proratedAmount += this.contactActivationFeePaymentRequest!.amount;
    }
    return (proratedAmount * (includeServiceCharge ? Number(this.serviceChargeAmount || 0) : 0) / 100) + proratedAmount;

  }

  getTotalActivationFeesAmount(amount: string, includeServiceCharge: boolean = false): number {
    const price = Number(this.commonService.convertCurrencyStringToNumber(amount));
    const serviceChargeAmount = this.contactActivationFeePaymentRequest?.payment_method.service_charge;

    return (price * (includeServiceCharge ? Number(serviceChargeAmount || 0) : 0) / 100) + price;

  }

  calcProrate(price: number): number {

    // Contact could be prorated - let's figure it out below
    let effectiveDates: any = this.contactFormGroup.controls['effectiveDates'].value;

    this.isProrated = false;


    // Contact has an effective date, does it occur in the future?
    const contactEffectiveDate = moment(effectiveDates);

    const selectedDate = contactEffectiveDate.get('date');

    if (selectedDate <= this.prorateAfterDays) {
      // The effective date is 16th or before so don't prorate since they will pay full price
      return price;
    } else {
      // The effective date is after 16th so we need to prorate
      // Does the start date come after the prorate date

      this.isProrated = true;
      // The effective date is after the prorate date
      return price / 2;

    }

  }

  openContactDialog(): void {
    this.initContactForm();

    this.contactFormGroup.get('rate')?.enable();

    if (this.source == 'attendant-portal') {
      
      if(this.tenantContactServiceInfo?.wps_active) {
        this.addKeycardsInfoGroup(false);
      }

      this.contactFormGroup.get('rate')?.setValidators([]);
      this.contactFormGroup.get('rate')?.updateValueAndValidity();

      this.contactFormGroup.get('effectiveDates')?.setValidators([Validators.required]);
      this.contactFormGroup.get('effectiveDates')?.setValue(new Date());
      this.contactFormGroup.get('effectiveDates')?.updateValueAndValidity();

      this.contactFormGroup.get('terminationDates')?.setValidators([Validators.required]);
      this.contactFormGroup.get('terminationDates')?.updateValueAndValidity();

      this.showAttendantAddContactModal = true;
    } else {

      this.contactFormGroup.get('effectiveDates')?.setValue(new Date());
      this.contactFormGroup.get('effectiveDates')?.updateValueAndValidity();

      // this.contactFormGroup.get('ar_type')?.setValidators([Validators.required]);
      // this.contactFormGroup.get('ar_type')?.updateValueAndValidity();

      this.goToContactStep();
      this.showContactModal = true;

      if (!this.validPaymentSettings) {
        this.messageService.add({
          severity: "error",
          detail: "Payment are disabled! due to location doesn't have valid settings.",
          key: 'disable-payment-msg',
          closable: false
        });
      }

      if(!this.activationFeesRateDescription) {
        this.messageService.add({
          severity: 'error',
          detail: 'Adding the new keycard not allowed due to the "Activation Fee" is not found.',
          key: 'edit-contact-msgs',
          closable: false
        });
      }
    }
  }

  closeContactDialog(): void {
    this.contactsUpdating = false;
    this.formSubmitted = false;
    this.showContactModal = false;
    this.showEditContactModal = false;
    this.selectedContactToUpdate = undefined;
    this.terminateAccountDate = undefined;
    this.showAttendantAddContactModal = false;
    this.primaryContact = undefined;
  }

  editContact(selectedContact: TennatAccountContactInfo): void {

    this.messageService.clear();

    let selectedRate: itemDescription | undefined = undefined;
    if (selectedContact['rate'] && this.tenantContactServiceInfo?.['available_invoice_items']) {
      const rateIndex = this.tenantContactServiceInfo?.available_invoice_items?.findIndex((item: any) => item.code === selectedContact['rate']);
      if (rateIndex !== -1) {
        selectedRate = this.tenantContactServiceInfo.available_invoice_items[rateIndex];
      }
    }

    let isTeminatedContact: boolean = false;
    if (selectedContact['terminationDates']) {
      isTeminatedContact = moment.utc(selectedContact['terminationDates']).isBefore(moment.utc());
    }
    this.initContactForm();

    //If there is keycards already assigned means this request is already approved
    //In this case we need to display all keycards
    selectedContact.keycardsInfo?.forEach((keycardInfo: KeycardInfo, index: number) => {

      const isDisabled = !(this.source == 'attendant-portal'); //Allow attendant to update eisting keycards
      const isNew = false;

      this.addKeycardsInfoGroup(isDisabled, isNew);

 
    });

    selectedContact?.vehiclesInfo?.forEach((vehicleInfo: VehicleInfo) => {
      this.addVehiclesInfoGroup();
    });

    console.log(selectedContact?.keycardsInfo);
    
    setTimeout(() => {

      this.contactFormGroup.setValue({
        contactName: selectedContact['contactName'],
        contactFirstName: this.commonService.splitNameToParts(selectedContact['contactName'], 'first'),
        contactLastName: this.commonService.splitNameToParts(selectedContact['contactName'], 'last'),
        description: selectedContact['description'],
        rate: selectedRate || '',
        ar_type: selectedContact['ar_type'] || '',
        phones: selectedContact['phones'],
        extensions: selectedContact['extensions'],
        emails: selectedContact['emails'],
        state: selectedContact['state'],
        effectiveDates: selectedContact['effectiveDates'],
        terminationDates: selectedContact['terminationDates'],
        isTerminated: isTeminatedContact,
        keycardsInfo: selectedContact?.keycardsInfo?.map((kc: KeycardInfo) => { kc['keycard_action'] = ''; kc['is_new'] = false; return kc; }) || [],
        vehiclesInfo: selectedContact?.vehiclesInfo || [],
      });  
      
    }, 0);
    

    //Ok so we dont want customer to allow editing terminated contact
    //So we have checked if source is not attedant
    if (isTeminatedContact && this.source !== 'attendant-portal') {
      this.contactFormGroup.disable();
      this.allowAddEditContact = false;
    }

    if (this.source != 'attendant-portal') {

      this.contactFormGroup.get('rate')?.disable();
      this.contactFormGroup.get('rate')?.clearValidators();
      this.contactFormGroup.get('rate')?.updateValueAndValidity();

      this.contactFormGroup.get('effectiveDates')?.disable();
      this.contactFormGroup.get('terminationDates')?.disable();
    }

    if (this.source == 'attendant-portal') {
      this.contactFormGroup.get('rate')?.clearValidators();
      this.contactFormGroup.get('rate')?.updateValueAndValidity();
    }

    this.contactFormGroup.updateValueAndValidity();

    //Hide When LPR Active on Attendant Portal
    if (!selectedContact?.keycardsInfo?.length && !this.isLPRActive) {
      this.messageService.add({
        severity: 'warn',
        detail: 'This contact has no keycard. If this contact needs a keycard please contact the parking attendant to have one assigned.',
        key: 'edit-contact-msgs',
        closable: false
      });
    }

    this.selectedContactToUpdate = selectedContact;
    this.showEditContactModal = true;
  }

  getContactErrors(contactContact: TennatAccountContactInfo) {

    if(!contactContact.surepark_member_details) {
      return "No matching surepark member found.";
    } else if(!contactContact.rateDisplay) {
      return "No rate found.";
    }

    return '';
  }

 selectContact(selectedContact?: TennatAccountContactInfo, mode: string = 'single', isSelected: boolean = true) {

    if (mode == "all") {
      const allContacts = [...this.tenantContacts];
      let skippedContacts: number = 0;

      for (let index = 0; index < allContacts.length; index++) {
        const contact = allContacts[index];
        
        if (!this.getContactErrors(contact)){
          contact.isSelected = isSelected;
        }
      }

      if(skippedContacts) {
          this.messageService.add({
            severity: "error",
            summary: "No Rate for Contact(s)",
            detail: `${skippedContacts} contacts skipped. Please set a monthly rate first and try again.`,
          });
      }
    } else {
      if(selectedContact) {
        if (!selectedContact?.isSelected) {
          if (!selectedContact.rate) {
            this.messageService.add({
              severity: "error",
              summary: "No Rate for Contact",
              detail: "You cannot invoice this contact. Please set a monthly rate first and try again.",
            });
            return;
          }
          selectedContact.isSelected = true;
        } else {
          selectedContact.isSelected = false;
        }
      }
    }

    const allContacts = [...this.tenantContacts, ...this.terminatedTenantContacts];
    const selectedContacts = allContacts.filter((contact) => contact.isSelected);
    this.contactsSelectedEvent.emit(selectedContacts);
  }

  goToConfirmationStep() {
    this.paymentMethodSelectionComponent.resetPaymentForm();
    this.contactFormGroup.reset();
    this.contactsUpdating = false;
    this.paymentInProgress = false;
    this.currentStepIndex = 3;
  }

  goToContactStep() {
    this.paymentMethodSelectionComponent?.resetPaymentForm();
    this.formSubmitted = false;
    this.currentStepIndex = 0;
  }

  async goToPaymentStep() {

    this.formSubmitted = true;
    this.contactFormGroup.markAllAsTouched();

    if (!this.contactFormGroup.valid) {
      return;
    }
    this.formSubmitted = false;

    this.validatingVehicleInfo = true;

    let formValues = Object.assign({}, this.contactFormGroup.getRawValue());

    await this.commService.validateVehiclesInfo(formValues['vehiclesInfo']
    ).then((validateVehicleInfoResponse: any) => {
      this.validatingVehicleInfo = false;
      this.currentStepIndex = 1;
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.validatingVehicleInfo = false;
    });

    /*
    let savePaymentMethodReq: paymentMethod | undefined = await this.paymentMethodSelectionComponent.validatePaymentForm();

    if(savePaymentMethodReq) {
      //If 'id' not exist means this is new payment method so lets save it before moving to next step
      if(!savePaymentMethodReq.id) {
        this.paymentInProgress = true;

        let contactPaymentResponse: any = await this.commService.savePaymentMethod(savePaymentMethodReq)?.catch((error: any) => {
          this.paymentMethodSelectionComponent.showCardFormError(error);
          this.paymentInProgress = false;
        });

        if(contactPaymentResponse.length) {
          //Refresh newly saved payment details so we can charge it
          savePaymentMethodReq = contactPaymentResponse[0];
        }
      }
      this.paymentInProgress = false;
      this.currentStepIndex = 2;


      const date: Date = new Date();

      let formValues = Object.assign({}, this.contactFormGroup.value);
      delete formValues['isTerminated'];

      if (formValues['year'] && typeof formValues['terminationDates'] != "string") {
        formValues['year'] = formValues['year'].getFullYear().toString();
      }

      formValues['ar_type'] = 'ACTIVATION_FEE';
      formValues['contactName'] = this.commonService.joinName(formValues['contactFirstName'], formValues['contactLastName']);

      formValues['rate'] = this.activationFeesRateDescription;

      formValues['rate_amount'] = this.commonService.convertCurrencyStringToNumber(formValues['rate']['unitprice']);
      formValues['rate_desc'] = formValues['rate']['description'];

      this.contactActivationFeePaymentRequest = {
        invoice_id: '',
        payment_method: savePaymentMethodReq!,
        amount: Number(this.getUnitPrice(this.activationFeesRateDescription!.unitprice)),
        date: date.toLocaleDateString("en-US"),
        contact_details: formValues
      }
    }*/
  }

  async goToActivatationFeeStep() {

    let savePaymentMethodReq: paymentMethod | undefined = await this.paymentMethodSelectionComponent.validatePaymentForm();

    if (savePaymentMethodReq) {

      const date: Date = new Date();

      let formValues = Object.assign({}, this.contactFormGroup.value);
      delete formValues['isTerminated'];

      if (formValues['year'] && typeof formValues['terminationDates'] != "string") {
        formValues['year'] = formValues['year'].getFullYear().toString();
      }

      formValues['ar_type'] = 'Monthly';
      formValues['contactName'] = this.commonService.joinName(formValues['contactFirstName'], formValues['contactLastName']);

      formValues['rate']['prorate'] = this.isProrated ? 'Yes' : 'No';

      this.contactPaymentRequest = {
        invoice_id: '',
        payment_method: savePaymentMethodReq!,
        amount: this.calcProrate(Number(this.getUnitPrice(formValues['rate']['unitprice']))),
        date: date.toLocaleDateString("en-US"),
        contact_details: formValues
      }

      if(savePaymentMethodReq!.name == "Bill Me") {
        this.currentStepIndex = 2;
      } else {
        this.processPayment();
      }

    }

  }

  async keycardAction(event: DropdownChangeEvent, keycardFormGroupIndex: number): Promise<void> {

    this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('active_keycard')?.setErrors({});

    let formValues = Object.assign({}, this.contactFormGroup.getRawValue());

    if (!formValues['keycardsInfo'][keycardFormGroupIndex]['keycards'] || !formValues['keycardsInfo'][keycardFormGroupIndex]['facility_code']) {
      this.formSubmitted = true;
      this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('active_keycard')?.setErrors({ 'custom_error': 'Keycard and Facility Code is required.' });
      this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('keycard_action')?.reset();
      return;
    }
    formValues['suite'] = formValues['suite'];
    formValues['account'] = formValues['account'];

    if (formValues['effectiveDates'] && typeof formValues['effectiveDates'] != "string") {
      formValues['effectiveDates'] = formValues['effectiveDates'].toLocaleDateString('en-US');
      formValues['effectiveDates'] = formValues['effectiveDates'].split("/").map((val: string) => {
        if (val.length < 2) {
          return this.pad(val);
        }
        return val;
      }).join("/");
    }

    if (formValues['terminationDates'] && typeof formValues['terminationDates'] != "string") {
      formValues['terminationDates'] = formValues['terminationDates'].toLocaleDateString('en-US');
      formValues['terminationDates'] = formValues['terminationDates'].split("/").map((val: string) => {
        if (val.length < 2) {
          return this.pad(val);
        }
        return val;
      }).join("/");
    }

    formValues['facility_code'] = formValues['keycardsInfo'][keycardFormGroupIndex]['facility_code'];
    formValues['keycards'] = formValues['keycardsInfo'][keycardFormGroupIndex]['keycards'];

    delete formValues['keycardsInfo'];

    if (formValues['vehiclesInfo']?.length) {

      if (formValues['year'] && typeof formValues['year'] != "string") {
        formValues['year'] = formValues['year'].getFullYear().toString();
      }
    }

    delete formValues['vehiclesInfo'];

    this.contactsUpdating = true;
    await this.commService.keyCardActions({
      facility_code: formValues['facility_code'],
      keycard: formValues['keycards'],
      action: event.value,
      contact: formValues
    }).then((updateTenantResponse: any) => {
      if (updateTenantResponse?.status == "failed" || updateTenantResponse?.status == "error") {
        let error: string = "Something went wrong, please try again!"
        if (event.value == "check") {
          error = "The keycard not found on the device.";
        }
        if (updateTenantResponse?.error?.length) {
          error = updateTenantResponse?.error;
        }
        this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('active_keycard')?.setErrors({ 'custom_error': error });
      }
      if (updateTenantResponse?.status == "ok" || updateTenantResponse?.status == "success") {
        let msg: string = "";
        if (event.value == "check") {
          if (updateTenantResponse['data']['customerState'] == "ACTIVE") {
            msg = "The keycard is on the device and ACTIVE.";
          } else {
            msg = "The keycard is on the device and " + updateTenantResponse['data']['customerState'];
          }
        } else if (event.value == "active") {
          msg = "The keycard is ACTIVATED.";
        } else if (event.value == "terminate") {
          msg = "The keycard is TERMINATED.";
        } else if (event.value == "add") {
          msg = "The keycard has been added to the parking device.";
          this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('active_keycard')?.setValue(true);
        }
        this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('active_keycard')?.setErrors({ 'custom_msg': msg });
      }
      this.getKeycardsInfoFormGroup(keycardFormGroupIndex).get('keycard_action')?.reset();
      this.contactsUpdating = false;
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.formSubmitted = false;
      this.contactsUpdating = false;
    });

  }

  async terminateAccounts(action: string): Promise<void> {

    let updatedContacts: any[] = [] = structuredClone(this.tenantContacts);

    if (this.selectedContactToUpdate) {
      const selectedContactIndex = updatedContacts.findIndex((contact: TennatAccountContactInfo) => JSON.stringify(contact) == JSON.stringify(this.selectedContactToUpdate));

      if (selectedContactIndex !== -1) {
        updatedContacts[selectedContactIndex] = this.updateTerminationDate(updatedContacts[selectedContactIndex], action);
      }
    } else {
      updatedContacts.forEach((tenantContact: TennatAccountContactInfo, index: number) => {
        updatedContacts[index] = this.updateTerminationDate(updatedContacts[index], action)
      });
    }

    this.contactsUpdating = true;
    this.showCancelKeycardModal = false;

    updatedContacts = structuredClone(updatedContacts.concat(this.terminatedTenantContacts));

    await this.saveContacts(updatedContacts);
  }

  async updateContact(event: Event, action: string): Promise<void> {
    this.formSubmitted = true;
    this.contactFormGroup.markAllAsTouched();

    if (!this.contactFormGroup.valid && action != 'delete') {
      return;
    }

    if(this.source == "attendant-portal" && this.primaryContact) {
       this.confirmationService.confirm({
            header: `Move this contact to ${this.primaryContact.contactName} contact?`,
            message: 'This will merge all keycard and vehicle details into primary contact, and this contact will get deleted PERMANENTLY.',
            accept: () => {
                this.mergeContact()
            },
            reject: () => {
              this.primaryContact = undefined;
            }
        });
        return;
    }
    
    let formValues = Object.assign({}, this.contactFormGroup.getRawValue());

    delete formValues['isTerminated'];

    if ((action == "create" || (action == "update" && this.source == "attendant-portal")) && this.tenantContactServiceInfo?.wps_active) {

      let hasInActiveKeycards: boolean = false;

      const today: Date = new Date();
      const utcDate: number = today.getUTCDate();

      formValues['keycardsInfo'].forEach((keycardInfo: KeycardInfo) => {
        /**
         * CONDITIONS:
         * 1. If active keycard not check
         * 2. Key card is newly added (because we dont want force active keycard for already added)
         * 3. Check if temination Date is in future
         *
         * If all condition matches means we have new keycard with valid details but not activated by customer
         * So in that case we will display an error message
        **/
        if (!keycardInfo['active_keycard'] && keycardInfo['is_new'] && utcDate < new Date(formValues['terminationDates']).getUTCDate()) {
          hasInActiveKeycards = true;
        }
      });

      if (hasInActiveKeycards) {
        this.messageService.add({
          severity: "error",
          detail: "Cannot save. The termination date is in the future but the keycard is not active. Please make sure the keycard is active before saving.",
        });
        return;
      }

      /**
       * CONDITIONS:
       * 1. If no keycards found
       * 2. Check if current user is not attendant by checking source because attendant can remove all keycards
       *     so there is not sense to display this message to attendant
       *
       * If all condition matches means there is not keycards added and customer tried to add/update contact
       * So in that case we will display an error message
      **/
      if (!formValues['keycardsInfo'].length && this.source != "attendant-portal") {
        this.messageService.add({
          severity: "error",
          detail: "Cannot save. No keycard found. Please make sure the keycard is active before saving.",
        });
        return;
      }

    }


    if (action != 'delete') {
      if (formValues['effectiveDates']) {

        if (typeof formValues['effectiveDates'] == "string") {
          formValues['effectiveDates'] = new Date(formValues['effectiveDates']).toLocaleDateString('en-US');
        } else if (typeof formValues['effectiveDates'] != "string") {
          formValues['effectiveDates'] = formValues['effectiveDates'].toLocaleDateString('en-US');
        }

        formValues['effectiveDates'] = formValues['effectiveDates'].split("/").map((val: string) => {
          if (val.length < 2) {
            return this.pad(val);
          }
          return val;
        }).join("/");
      }

      if (formValues['terminationDates']) {
        if (typeof formValues['terminationDates'] == "string") {
          formValues['terminationDates'] = new Date(formValues['terminationDates']).toLocaleDateString('en-US');
        } else if (typeof formValues['terminationDates'] != "string") {
          formValues['terminationDates'] = formValues['terminationDates'].toLocaleDateString('en-US');
        }

        formValues['terminationDates'] = formValues['terminationDates'].split("/").map((val: string) => {
          if (val.length < 2) {
            return this.pad(val);
          }
          return val;
        }).join("/");
      }


      formValues['vehiclesInfo'] = formValues['vehiclesInfo'].map((vehicleData: any) => {
        if (vehicleData['year'] && typeof vehicleData['year'] != "string") {
          vehicleData['year'] = vehicleData['year'].getFullYear().toString();
        }
        return vehicleData;
      })
      console.log(formValues)
    }

    this.contactsUpdating = true;

    let updatedContacts: any[] = [] = structuredClone(this.tenantContacts.concat(this.terminatedTenantContacts));

    if (action == "update") {
      let propertiesToUpdate: any = {
        contactName: this.commonService.joinName(formValues['contactFirstName'], formValues['contactLastName']),
        contactFirstName: formValues['contactFirstName'],
        contactLastName: formValues['contactLastName'],
        description: formValues['description'],
        emails: formValues['emails'],
        extensions: formValues['extensions'],
        phones: formValues['phones'],
        make: formValues['make'],
        model: '',
        year: '',
        color: '',
        plate: '',
        state: formValues['state'],
        vehiclesInfo: formValues['vehiclesInfo'],
      }

      //Attendant portal user should be able to update those details of contact
      if (this.source == 'attendant-portal') {

        propertiesToUpdate['effectiveDates'] = formValues['effectiveDates'];
        propertiesToUpdate['terminationDates'] = formValues['terminationDates'];
        propertiesToUpdate['keycardsInfo'] = formValues['keycardsInfo'];

        propertiesToUpdate['rate'] = '';
        propertiesToUpdate['rate_amount'] = '';
        propertiesToUpdate['rate_desc'] = '';
        
        if (formValues['rate']) {
          formValues['rate']['prorate'] = this.isProrated ? 'Yes' : 'No';
          propertiesToUpdate['rate_amount'] = this.calcProrate(Number(this.getUnitPrice(formValues['rate']['unitprice'])));
          propertiesToUpdate['rate_desc'] = formValues['rate']['description'];
          propertiesToUpdate['rate'] = formValues['rate']['code'];
        }

      }

      const selectedContactIndex = updatedContacts.findIndex((contact: TennatAccountContactInfo) => JSON.stringify(contact) == JSON.stringify(this.selectedContactToUpdate));

      updatedContacts[selectedContactIndex] = Object.assign(updatedContacts[selectedContactIndex], propertiesToUpdate);
      updatedContacts[selectedContactIndex]['contactFirstName'] = formValues['contactFirstName'];
      updatedContacts[selectedContactIndex]['contactLastName'] = formValues['contactLastName'];

      //We will check this flag and update this contact details on surepark.
      updatedContacts[selectedContactIndex]['updateOnSurepark'] = true;

    } else if (action == "create") {
      formValues['contactName'] = this.commonService.joinName(formValues['contactFirstName'], formValues['contactLastName']);
      if (formValues['rate']) {
        formValues['rate_amount'] = this.commonService.convertCurrencyStringToNumber(formValues['rate']['unitprice']);
        formValues['rate_desc'] = formValues['rate']['description'];
        formValues['rate'] = formValues['rate']['code'];
      } else {
        formValues['rate_amount'] = '';
        formValues['rate_desc'] = '';
      }
      //We will check this flag and add this contact details on surepark.
      formValues['updateOnSurepark'] = true;

      updatedContacts.push(formValues);
    }

    let allPlatesAvailable = false;
    let notInSelectedContactDetails: VehicleInfo[] = [];
    let newContactVehicleDetails: VehicleInfo[] = [];

    if(action == "update" || action == "create") {

      const updatedContactDetails = formValues['vehiclesInfo'];

      // Deep equality function
      const isEqual = (a: any, b: any): boolean =>
        Object.keys(a).length === Object.keys(b).length &&
        Object.keys(a).every(key => a[key] === b[key]);

      // Get objects in arr2 not present in arr1
      notInSelectedContactDetails = updatedContactDetails.filter((obj2 : VehicleInfo) =>
        !this.selectedContactToUpdate?.vehiclesInfo?.some(obj1 => isEqual(obj1, obj2))
      );

      //Skip already saved vehicle details because we want to validate only changed vehicle plate info
      newContactVehicleDetails = notInSelectedContactDetails.filter((obj2 : VehicleInfo) =>
        !this.selectedContactToUpdate?.vehiclesInfo?.some(obj1 => obj1['plate'] == obj2['plate'])
      );

      //Display an error of any license plate is already in use
      allPlatesAvailable = await this.validateVehicleInfo(newContactVehicleDetails)

      //If attendant portal is updating in that case we dont want to create request but directly save on SUREPARK
      if(action == "update"  && this.source != "attendant-portal") {
        //Remove temp property so we can compare selected contact details
        const selectedContactIndex = updatedContacts.findIndex((contact: TennatAccountContactInfo) => {
          return contact['keycards'] == this.selectedContactToUpdate?.['keycards']
        });
        if(selectedContactIndex !== -1) {

          const isVehicleChanged = this.hasVehicleChanged(this.selectedContactToUpdate?.vehiclesInfo || [], updatedContactDetails);
          
          updatedContacts[selectedContactIndex]['modifiedVehicleInfo'] = isVehicleChanged ? updatedContactDetails : [];
          updatedContacts[selectedContactIndex]['vehiclesInfo'] = this.selectedContactToUpdate?.['vehiclesInfo'];
        }

      }

      /**
       * PROBLEM DESCRIPTION
       * WE got an issue in case if attendant removes all keycards and update contact system unable to match SUREPARK subscriber
       * because we have matched based on keycard and facility code and system will create subscriber instead of update the correct one if fails to find correct SUREPARK subscriber
       *
       * SOLUTION
       * If attendant is updating keycard info in that case we have to keep old keycard info
       * So we can match SUREPARK subscriber basd on keycard and facility code
       * Here we have kept old keycard info in `keycardsInfo` and updated keycardinfo in `modifiedKeycardsInfo` property
       */
      if(action == "update"  && this.source == "attendant-portal") {

        const selectedContactIndex = updatedContacts.findIndex((contact: TennatAccountContactInfo) => contact.updateOnSurepark);

        if(selectedContactIndex !== -1) {
          updatedContacts[selectedContactIndex]['keycardsInfo'] = this.selectedContactToUpdate?.['keycardsInfo'];
          updatedContacts[selectedContactIndex]['modifiedKeycardsInfo'] = formValues['keycardsInfo'];
        }

      }
    }

    if(allPlatesAvailable) {
      await this.saveContacts(updatedContacts);
    }

  }

  async validateVehicleInfo(newContactVehicleDetails: VehicleInfo[]) {
    this.contactsUpdating = true;

    return await this.commService.validateVehiclesInfo(newContactVehicleDetails
    ).then(async (validateVehicleInfoResponse: any) => {
      return validateVehicleInfoResponse;
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.contactsUpdating = false;
    });
  }

  private hasVehicleChanged(oldList: VehicleInfo[], newList: VehicleInfo[]): boolean {
    const oldMap = new Map(oldList.map(v => [v.plate, v]));

    // Check for updates or deletions
    for (const [plate, oldVehicle] of oldMap.entries()) {
      const newVehicle = newList.find(v => v.plate === plate);

      // Deleted vehicle
      if (!newVehicle) return true;

      // Any changed field
      if (
        oldVehicle.make !== newVehicle.make ||
        oldVehicle.model !== newVehicle.model ||
        oldVehicle.year !== newVehicle.year ||
        oldVehicle.color !== newVehicle.color
      ) {
        return true;
      }
    }

    // Check for newly added vehicles
    for (const newVehicle of newList) {
      if (!oldMap.has(newVehicle.plate)) return true;
    }

    return false; // No change detected
  }

  async saveContacts(updatedContacts: any[]) {
    await this.commService.saveContacts({ contacts: updatedContacts, suite: this.suite, account: this.account }).then((updateTenantResponse: any) => {
      console.log(updateTenantResponse);
      
      //set contactUpdated event to true so billing summary will get refresh
      this.contactUpdatedEvent.emit(true);
      this.loadTenantInfo();

      this.messageService.add({
        severity: "success",
        detail: "Account contacts updated successfully.",
      });
      this.closeContactDialog();
    }).catch((error: any) => {
      this.messageService.add({
        severity: "error",
        detail: error.message,
      });
      this.formSubmitted = false;
      this.contactsUpdating = false;
    });
  }

  async mergeContact() {

    if (!this.selectedContactToUpdate?.keycardsInfo?.length && !this.selectedContactToUpdate?.vehiclesInfo?.length) {
      this.messageService.add({
        severity: "error",
        detail: "Unable to merge due to no keycard and vehicle found in this contact.",
        closable: false
      });
      return;
    }

    this.contactsUpdating = true;

    try {

      const pproSourceContactInfo: TennatAccountContactInfo =  this.selectedContactToUpdate;
      const pproPrimaryContactInfo: TennatAccountContactInfo =  this.primaryContact!;
      
      this.commService.mergeContacts({
        suite: this.suite!,
        account: this.account!,
        sourceContact: pproSourceContactInfo,
        primaryContact: pproPrimaryContactInfo
      }).then((response: any) => {

        this.messageService.add({
          severity: "success",
          detail: "Contact(s) merged successfully."
        });

        setTimeout(() => {
          
          //set contactUpdated event to true so billing summary will get refresh
          this.contactUpdatedEvent.emit(true);

          this.closeContactDialog();
          this.loadTenantInfo();
        }, 3000);
        

        }).catch((error: any) => {
          this.messageService.add({
            severity: "error",
            detail: "Unable to merge contact.",
          });
          this.contactsUpdating = false;
        });
      

    } catch (error) {
      this.contactsUpdating = false;
    }

  }
  
  updateTerminationDate(contact: TennatAccountContactInfo, action: string): TennatAccountContactInfo {
    let isTerminated: boolean = false;

    if (!contact.terminationDates) {
      if (action == "immediate") {
        contact['terminationDates'] = (new Date().getUTCMonth() + 1) + "/" + new Date().getUTCDate() + "/" + new Date().getUTCFullYear()
        isTerminated = true;
      }
      else if (action == "date") {
        contact['terminationDates'] = (this.terminateAccountDate?.getUTCMonth()! + 1) + "/" + this.terminateAccountDate?.getUTCDate() + "/" + this.terminateAccountDate?.getUTCFullYear()
        isTerminated = true;
      }
    } else {
      const [month, day, year] = contact['terminationDates'].split("/");
      const terminationDate = new Date();
      terminationDate.setUTCFullYear(Number(year), Number(month) - 1, Number(day));
      terminationDate.setHours(0, 0, 0, 0);

      let currentUTCDate = new Date();
      if (action == "immediate") {
        currentUTCDate.setUTCFullYear(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate());
      } else if (action == "date") {
        currentUTCDate.setUTCFullYear(this.terminateAccountDate?.getUTCFullYear()!, this.terminateAccountDate?.getUTCMonth(), this.terminateAccountDate?.getUTCDate());
      }

      currentUTCDate.setHours(0, 0, 0, 0);

      //Check if termination date is in future if yes then change it to todays UTC date
      if (currentUTCDate < terminationDate) {
        contact['terminationDates'] = (currentUTCDate.getUTCMonth() + 1) + "/" + currentUTCDate.getUTCDate() + "/" + currentUTCDate.getUTCFullYear()
        isTerminated = true;
      }
    }

    //We will check this flag and update this contact details on surepark.
    contact['updateOnSurepark'] = isTerminated;

    return contact;
  }

  async processPayment(): Promise<void> {
    try {

        this.paymentInProgress = true;

        //we only need immediate payment if they do the ACH bill me option.
        //If they choose each check or credit card for payment, then the activation fee can be part of the pre-authorization charge
        if(this.contactPaymentRequest?.payment_method.name == "Bill Me") {

          let savePaymentMethodReq: paymentMethod | undefined = await this.paymentMethodSelectionComponent.validatePaymentForm();

          if (savePaymentMethodReq) {

            const date: Date = new Date();

            let formValues = Object.assign({}, this.contactFormGroup.value);
            delete formValues['isTerminated'];

            if (formValues['year'] && typeof formValues['terminationDates'] != "string") {
              formValues['year'] = formValues['year'].getFullYear().toString();
            }

            formValues['ar_type'] = 'ACTIVATION_FEE';
            formValues['contactName'] = this.commonService.joinName(formValues['contactFirstName'], formValues['contactLastName']);

            formValues['rate'] = this.activationFeesRateDescription;
            formValues['rate_amount'] = this.commonService.convertCurrencyStringToNumber(formValues['rate']['unitprice']);
            formValues['rate_desc'] = formValues['rate']['description'];

            const contactActivationFeePaymentRequest: ContactPaymentRequest = {
              invoice_id: '',
              payment_method: savePaymentMethodReq!,
              amount: Number(this.getUnitPrice(formValues['rate']['unitprice'])),
              date: date.toLocaleDateString("en-US"),
              contact_details: formValues,
              charge_activation_fee: false
            }

            const contactActivationFeePaymentResponse: any = await this.commService.chargeContactActivationFees(contactActivationFeePaymentRequest!)?.catch((error: any) => {
              this.paymentMethodSelectionComponent.showCardFormError(error);
              this.paymentInProgress = false;
            });

            if(!contactActivationFeePaymentResponse) {
              this.paymentInProgress = false;
              return;
            }
          }

        } else {
          this.contactPaymentRequest!.charge_activation_fee = true;
        }

        let contactPaymentResponse: any = await this.commService.chargeContact(this.contactPaymentRequest!)?.catch((error: any) => {
          this.paymentMethodSelectionComponent.showCardFormError(error);
          this.paymentInProgress = false;
        });

        if (contactPaymentResponse) {
          this.goToConfirmationStep();
          setTimeout(() => {
            this.messageService.add({
              severity: "success",
              detail: "We have received your change request, you will get notified once change is approved.",
              key: 'confirm-message',
              closable: false
            });
          }, 1000);
        }


    } catch (error: any) {
      this.paymentMethodSelectionComponent.showCardFormError(error)
      this.paymentInProgress = false;
    }
  }

  pad(str: string): string {
    if (str.length === 2) return str;
    return this.pad('0' + str);
  }
}
